import Vue from 'vue';
import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import moment from 'moment';
import { saveAs } from 'file-saver';

interface ComplaintsExtendReportState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;


    filter: {
        dZoneGroups: any;
        targetDates: Date[];
        clients: any[];
        reason_ids: any[];
        product_id: any;
        product_container_id: any;
        deal_date: any;
        sticker_id: any;
    };

    items: any[];
}

const state: ComplaintsExtendReportState = {
    loading: false,
    loaded: false,
    fatalError: false,

    filter: {
        dZoneGroups: null,
        targetDates: [new Date(), new Date()],
        clients: [],
        reason_ids: [],
        product_id: null,
        product_container_id: null,
        deal_date: null,
        sticker_id: null,
    },

    items: [],
};

function buildQuery (filter: any) {
    return {
      columns: {
        deal_delivery_date: 'd.expected_delivery_date',
        client_id: 'cl.id',
        client_name: 'cl.name',
        reason_name: 'cr.name',
        id: 'c.id',
        description: 'c.description',
        attachments: 'c.attachments',
        complaint_date: 'c.complaint_date',
        product_type: {
          $case: [
            [{ $null: ["pt.parent_id"] }, "pt.name"],
            [{ $: true }, "root_pt.name"]
          ]
        },
        product_name: 'p.name',
        product_container_name: 'pc.name',
        product_date: 'c.product_date',
        sticker_name: 'vs.name',
        plan_number: 'c.plan_number',
        decision: 'c.decision',
        shipment: 'c.shipment',
      },
      from: 's:complaints',
      from_as: 'c',
      joins: [
        {
          join: 's:complaints_deals',
          as: 'cd',
          on: {
            $eq: [
              'c.id',
              'cd.complaint_id'
            ]
          }
        },
        {
          join: 's:deals',
          as: 'd',
          on: {
            $eq: [
              'cd.deal_id',
              'd.id'
            ]
          }
        },
        {
          join: 's:clients',
          as: 'cl',
          on: {
            $eq: [
              'd.client_id',
              'cl.id'
            ]
          }
        },
        {
          join: 's:complaint_complaint_reasons',
          as: 'ccr',
          on: {
            $eq: [
              'ccr.complaint_id',
              'c.id'
            ]
          }
        },
        {
          join: 's:complaint_reasons',
          as: 'cr',
          on: {
            $eq: [
              'cr.id',
              'ccr.complaint_reason_id'
            ]
          }
        },
        {
          join: 's:products',
          as: 'p',
          type: 'left',
          on: {
            $eq: [
              'p.id',
              'c.product_id'
            ]
          }
        },
        {
          join: 's:products_types',
          as: 'pt',
          type: 'left',
          on: {
            $eq: [
              'pt.id',
              'p.type_id'
            ]
          }
        },
        {
          join: 's:products_types',
          as: 'root_pt',
          type: 'left',
          on: {
            $eq: [
              'root_pt.id',
              'pt.parent_id'
            ]
          }
        },
        {
          join: 's:product_containers',
          as: 'pc',
          type: 'left',
          on: {
            $eq: [
              'pc.id',
              'c.product_container_id',
            ]
          }
        },
        {
          join: 's:product_containers_stickers',
          as: 'pcs',
          type: 'left',
          on: {
            $eq: [
              'pcs.product_container_id',
              'pc.id',
            ]
          }
        },
        {
          join: 's:voc_stickers',
          as: 'vs',
          type: 'left',
          on: {
            $eq: [
              'vs.id',
              'pcs.sticker_id',
            ]
          }
        },
      ],
      where: {
        $and: [
          ...(
            filter.date_from ? [
              {
                $gte: [
                  'c.complaint_date',
                  {
                    $: filter.date_from
                  }
                ]
              }
            ] : []
          ),
          ...(
            filter.date_to ? [
              {
                $lte: [
                  'c.complaint_date',
                  {
                    $: filter.date_to
                  }
                ]
              }
            ] : []
          ),
          ...(
            filter.clients && filter.clients.length ? [
              {
                $in: [
                  'cl.id',
                  {
                    $: filter.clients
                  }
                ]
              }
            ] : []
          ),
          ...(
            filter.reason_ids && filter.reason_ids.length ? [
              {
                $in: [
                  'cr.id',
                  {
                    $: filter.reason_ids
                  }
                ]
              }
            ] : []
          ),
          ...(
            filter.product_id ? [
              {
                $eq: [
                  'c.product_id',
                  {
                    $: filter.product_id
                  }
                ]
              }
            ] : []
          ),
          ...(
            filter.product_container_id ? [
              {
                $eq: [
                  'c.product_container_id',
                  {
                    $: filter.product_container_id
                  }
                ]
              }
            ] : []
          ),
          ...(
            filter.deal_date ? [
              {
                $eq: [
                  'd.expected_delivery_date',
                  {
                    $: filter.deal_date
                  }
                ]
              }
            ] : []
          ),
          ...(
            filter.sticker_id ? [
              {
                $eq: [
                  'vs.id',
                  {
                    $: filter.sticker_id
                  }
                ]
              }
            ] : []
          ),
        ]
      },
      page_size: 100000,
      page: 1
    };
}

function buildExportQuery(filter: any) {
    return {
      template: '<!DOCTYPE html>\n<table font=Verdana font-size=8>\n  <tr>\n    <td wrap bgcolor=FF72BF44 width=10>Дата</td>\n    <td wrap bgcolor=FF72BF44 width=45>Клиент</td>\n    <td wrap bgcolor=FF72BF44 width=30>Причина</td>\n    <td wrap bgcolor=FF72BF44 width=150>Описание</td>\n  </tr>\n  {{#clients.documents}}\n    <tr>\n      <td wrap>{{deal_delivery_date}}</td>\n      <td wrap>{{client_name}}</td>\n      <td wrap>{{reason_name}}</td>\n      <td wrap>{{description}}</td>\n    </tr>\n  {{/clients.documents}}\n</table>\n',
      queries: {
        clients: buildQuery(filter)
      },
      format: 'xlsx'
    };
}

function buildFilter(params: any) {
    const queryParams: any = {}

    if (params.targetDates) {
        queryParams.date_from = moment(params.targetDates[0]).format('YYYY-MM-DD');
        queryParams.date_to = moment(params.targetDates[1]).format('YYYY-MM-DD')
    }

    if (params.dZoneGroups) {
        queryParams.delivery_zone_id = params.dZoneGroups;
    }
    if (params.product_id) {
      queryParams.product_id = params.product_id;
    }

    if (params.product_container_id) {
      queryParams.product_container_id = params.product_container_id;
    }

    if (params.sticker_id) {
      queryParams.sticker_id = params.sticker_id;
    }

    if (params.deal_date) {
      queryParams.deal_date = moment(params.deal_date).format('YYYY-MM-DD');
    }

    if (params.clients) {
        queryParams.clients = Array.isArray(params.clients) ? params.clients : [params.clients]
    }

    if (params.reason_ids) {
        queryParams.reason_ids = Array.isArray(params.reason_ids) ? params.reason_ids : [params.reason_ids]
    }

    if (params.sortField !== null) {
        queryParams.order_by = params.sortField;
        queryParams.order_type = params.sortDirection;
    }

    return queryParams;
}

export const REPORT_DATA = 'REPORT_DATA';

export const actions: ActionTree<ComplaintsExtendReportState, RootState> = {

    loadData({ commit, state, dispatch }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {

            if (Object.values(params).length > 0) {
                commit('START_LOADING');

                vm.$db([
                    vm.$q(REPORT_DATA)
                        .raw('documents.list')
                        .action(buildQuery(buildFilter(params))),
                ]).then(((result: ServerResult) => {
                    commit('SET_REPORT_DATA', result.getOperation(REPORT_DATA).getContent());
                    commit('LOADED_SUCCESS');

                    resolve();
                }));
            } else {
                resolve();
            }
        })
    },

    exportDataAsXLSX({rootState}, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            vm.$db([
                vm.$q('EXPORT')
                .raw('documents.report')
                .action(buildExportQuery(buildFilter(params)))
            ]).then(((result: ServerResult) => {
                const exportOpContent = result.getOperation('EXPORT').getContent();
                const fToken = exportOpContent.token;
                const aToken = (rootState as any).ubic.token
                const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${fToken}?authorization=${aToken}`;
                saveAs(fileUrl, 'export.xlsx')

                resolve();
            }));
        })
    },
};

export const mutations: MutationTree<ComplaintsExtendReportState> = {
    START_LOADING: (state) => {
        // state.loaded = false;
        state.fatalError = false;
    },
    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.fatalError = false;
    },
    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.fatalError = true;
    },

    SET_REPORT_DATA(state, data) {
        state.items = data.documents;
    },
};

const getters = {

};

export const STORE_KEY = '$_complains_extend_report';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
